import { useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import useUploadFiles from 'api/hooks/useUploadFiles';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import UploadHubModalAttach from 'components/UploadHub/UploadHubModalAttach';
import type { AttachedFile } from 'components/UploadHub/UploadHubModalAttach';
import { getFileBadge } from 'components/UploadHub/fileTypeBadges';
import { buildDocumentSharePageV2Url } from 'utils/buildShareDocumentV2Link';

interface InteractionAttachmentsProps {
  attachments?: string[];
  onChange: (attachments: string[]) => void;
  disabled?: boolean;
}

const InteractionAttachments = ({ attachments = [], onChange, disabled }: InteractionAttachmentsProps) => {
  const { getUploadHubList } = useUploadFiles();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filesByUrl, setFilesByUrl] = useState<Record<string, AttachedFile>>({});
  const [isLoadingFiles, setIsLoadingFiles] = useState(attachments.length > 0);

  const fetchFiles = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchFiles.current = async (signal) => {
    try {
      const results: UploadHubListItem[] = await getUploadHubList({ signal });
      const teamFiles = Object.fromEntries(
        (results ?? []).map(({ publicId, fileName, contentType }) => {
          const url = buildDocumentSharePageV2Url(publicId);
          return [url, { url, fileName, contentType }];
        })
      );
      setFilesByUrl((current) => ({ ...teamFiles, ...current }));
      setIsLoadingFiles(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    if (attachments.length === 0) return;
    const controller = new AbortController();
    fetchFiles.current?.(controller.signal);
    return () => controller.abort('Component unmounted.');
  }, []);

  const addAttachments = (files: AttachedFile[]) => {
    setFilesByUrl((current) => ({ ...current, ...Object.fromEntries(files.map((file) => [file.url, file])) }));
    const newUrls = files.map(({ url }) => url).filter((url) => !attachments.includes(url));
    if (newUrls.length > 0) onChange(attachments.concat(newUrls));
  };

  return (
    <div className='mb-3'>
      <div className='flex-centered mb-2 '>
        <label className='d-block mr-2 paragraph-p2 font-weight-bold mb-0'>Attachments</label>
      </div>

      {!disabled && (
        <button
          type='button'
          className='general-button simple-link-button paragraph-p2 px-0 py-0'
          onClick={() => setIsModalOpen(true)}
        >
          + Attach a file
        </button>
      )}

      {attachments.length > 0 && (
        <ul className='policy-list px-0 mb-3'>
          {attachments.map((url) => {
            const file = filesByUrl[url];
            if (!file && isLoadingFiles) {
              return (
                <li className='border-main-blue-50 box-rounded mb-3 attachment-name flex-centered' key={url}>
                  <Skeleton width={20} height={20} borderRadius={3} className='mr-2' />
                  <Skeleton width={180} height={14} />
                </li>
              );
            }
            const badge = getFileBadge({ contentType: file?.contentType });
            const fileName = file?.fileName ?? 'File';
            return (
              <li
                className='border-main-blue-50 box-rounded d-flex justify-content-between mb-3 align-items-center'
                key={url}
              >
                <a
                  href={url}
                  target='_blank'
                  rel='noreferrer'
                  className='attachment-name mb-0 flex-centered text-main-blue general-link'
                >
                  <span
                    className={`mr-2 font-weight-bold upload-hub-file-icon upload-hub-file-icon-sm ${badge.className} text-white text-center flex-centered justify-content-center`}
                  >
                    {badge.label}
                  </span>
                  <span className='hidden-lines hidden-one-line'>{fileName}</span>
                </a>
                {!disabled && (
                  <div className='px-3'>
                    <button
                      type='button'
                      className='general-button icon-delete upload-hub-remove-file'
                      aria-label='Remove attachment'
                      data-tooltip-id='general-tooltip'
                      data-tooltip-content='Remove'
                      onClick={() => onChange(attachments.filter((attachment) => attachment !== url))}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <UploadHubModalAttach openState={{ isOpen: isModalOpen, setIsOpen: setIsModalOpen }} onAttach={addAttachments} />
    </div>
  );
};

export default InteractionAttachments;
